import { Injectable } from '@angular/core';
import { AmsFilesListItemMenuClick } from '@core/components/files/lists/files-list-item-menu-click';

/**
 * PendingActionsService
 * 
 * Persists pending file actions (like download) across component destruction/recreation.
 * 
 * Problem: When a parent component refreshes signatures, it may destroy and recreate
 * the signed-files-list child component. This causes the pending action state to be lost.
 * 
 * Solution: Store pending actions in this service (not component property) so they survive
 * component lifecycle changes. When a new component instance is created, it restores
 * the pending action from this service.
 * 
 * Usage:
 * 1. When action is blocked due to expired signature: setPendingAction(fileId, action)
 * 2. When component initializes: getPendingAction() to restore state
 * 3. When action is executed: clearPendingAction() to clean up
 */
@Injectable({
    providedIn: 'root'
})
export class PendingActionsService {
    private pendingAction: { fileId: string; action: AmsFilesListItemMenuClick } | null = null;

    /**
     * Store a pending action
     * This action will be restored when a new component instance is created
     * 
     * @param fileId ID of the file associated with this action
     * @param action The action object to execute when signature refreshes
     */
    setPendingAction(fileId: string, action: AmsFilesListItemMenuClick): void {
        this.pendingAction = { fileId, action };
    }

    /**
     * Retrieve and return the pending action
     * Does NOT remove it from service - caller is responsible for clearing after use
     * 
     * @returns The pending action if one exists, null otherwise
     */
    getPendingAction(): { fileId: string; action: AmsFilesListItemMenuClick } | null {
        if (this.pendingAction) {
            // console.log('$$$ [PendingActionsService] Retrieving pending action:', this.pendingAction.fileId);
        }
        return this.pendingAction;
    }

    /**
     * Clear the pending action
     * Called after the action has been successfully executed
     */
    clearPendingAction(): void {
        if (this.pendingAction) {
            // console.log('$$$ [PendingActionsService] Clearing pending action:', this.pendingAction.fileId);
        }
        this.pendingAction = null;
    }

    /**
     * Check if there's a pending action
     */
    hasPendingAction(): boolean {
        return this.pendingAction !== null;
    }
}
